import { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Save, X, Shield, User, Mail, ChevronDown, ChevronUp, Bell, UserCog, Clock } from 'lucide-react';

import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card } from '../ui/card';
import { Checkbox } from '../ui/checkbox';
import { adminFetch, getAuthHeaders } from '../../services/adminApi';

import { PageTitle } from './shared';
import { adminRoutes } from './navigation/adminRoutes';

const AdminUsersEditor = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [addingNew, setAddingNew] = useState(false);
  const [message, setMessage] = useState('');
  const [formData, setFormData] = useState({ email: '', name: '' });

  // Permission management state
  const [editingPermissions, setEditingPermissions] = useState(null);
  const [tempPermissions, setTempPermissions] = useState([]);

  // Release notes preference state - tracks which user ID is being updated (null if none)
  const [updatingReleaseNotesFor, setUpdatingReleaseNotesFor] = useState(null);

  // Get current user's protected status from localStorage
  const currentUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
  const isCurrentUserProtected = currentUser.is_protected === true;

  // Format last logged in date
  const formatLastLogin = (dateStr) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;

    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)} minutes ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)} hours ago`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)} days ago`;
    return date.toLocaleDateString() + ' at ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await adminFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      } else {
        setMessage('Failed to load admin users');
      }
    } catch (error) {
      setMessage('Failed to load admin users');
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async () => {
    if (!formData.email || !formData.name) {
      setMessage('Please provide both email and name');
      return;
    }

    try {
      const response = await adminFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setMessage('Admin user added successfully!');
        setFormData({ email: '', name: '' });
        setAddingNew(false);
        fetchUsers();
        setTimeout(() => setMessage(''), 3000);
      } else {
        const error = await response.json();
        setMessage(error.detail || 'Failed to add user');
      }
    } catch (error) {
      setMessage('Failed to add user');
      console.error('Error adding user:', error);
    }
  };

  const handleUpdateUser = async (userId) => {
    const user = users.find(u => u.id === userId);
    
    try {
      const response = await adminFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${userId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ name: user.name })
      });

      if (response.ok) {
        setMessage('User updated successfully!');
        setEditingId(null);
        fetchUsers();
        setTimeout(() => setMessage(''), 3000);
      } else {
        setMessage('Failed to update user');
      }
    } catch (error) {
      setMessage('Failed to update user');
      console.error('Error updating user:', error);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this admin user?')) {
      return;
    }

    try {
      const response = await adminFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        setMessage('User deleted successfully!');
        fetchUsers();
        setTimeout(() => setMessage(''), 3000);
      } else {
        const error = await response.json();
        setMessage(error.detail || 'Failed to delete user');
      }
    } catch (error) {
      setMessage('Failed to delete user');
      console.error('Error deleting user:', error);
    }
  };

  const handleEditName = (userId, newName) => {
    setUsers(users.map(u => u.id === userId ? { ...u, name: newName } : u));
  };

  // Permission management handlers
  const handleToggleSection = (sectionId) => {
    setTempPermissions(prev => {
      if (prev.includes(sectionId)) {
        return prev.filter(id => id !== sectionId);
      } else {
        return [...prev, sectionId];
      }
    });
  };

  const handleToggleCategory = (categoryKey) => {
    const categorySections = adminRoutes[categoryKey].sections.map(s => s.id);
    const allSelected = categorySections.every(id => tempPermissions.includes(id));

    if (allSelected) {
      // Remove all sections in this category
      setTempPermissions(prev => prev.filter(id => !categorySections.includes(id)));
    } else {
      // Add all sections in this category
      setTempPermissions(prev => [...new Set([...prev, ...categorySections])]);
    }
  };

  const handleSavePermissions = async (userId) => {
    try {
      const response = await adminFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${userId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ allowed_sections: tempPermissions })
      });

      if (response.ok) {
        setMessage('Permissions updated successfully!');
        setEditingPermissions(null);
        fetchUsers();
        setTimeout(() => setMessage(''), 3000);
      } else {
        setMessage('Failed to update permissions');
      }
    } catch (error) {
      setMessage('Failed to update permissions');
      console.error('Error updating permissions:', error);
    }
  };

  const getCategoryPermissionCount = (categoryKey) => {
    const categorySections = adminRoutes[categoryKey].sections.map(s => s.id);
    const selectedCount = categorySections.filter(id => tempPermissions.includes(id)).length;
    return { selected: selectedCount, total: categorySections.length };
  };

  const handleToggleReleaseNotes = async (userId, userEmail, enabled) => {
    setUpdatingReleaseNotesFor(userId);
    try {
      // Use /me endpoint for self, /{user_id} endpoint for others
      const isSelf = userEmail === currentUser.email;
      const url = isSelf
        ? `${process.env.REACT_APP_BACKEND_URL}/api/admin/users/me/release-notes?enabled=${enabled}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${userId}/release-notes?enabled=${enabled}`;

      const response = await adminFetch(url, {
        method: 'PUT',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        // Update local storage if updating self
        if (isSelf) {
          const updatedUser = { ...currentUser, receive_release_notes: enabled };
          localStorage.setItem('adminUser', JSON.stringify(updatedUser));
        }
        setMessage(enabled ? 'Release notes enabled!' : 'Release notes disabled');
        fetchUsers();
        setTimeout(() => setMessage(''), 3000);
      } else {
        const error = await response.json();
        setMessage(error.detail || 'Failed to update preference');
      }
    } catch (error) {
      setMessage('Failed to update preference');
      console.error('Error updating release notes preference:', error);
    } finally {
      setUpdatingReleaseNotesFor(null);
    }
  };

  if (loading) {
    return <div className="flex justify-center p-8">Loading admin users...</div>;
  }

  return (
    <div className="space-y-6">
      <PageTitle
        title="Admin Users"
        description="Manage administrator accounts and access"
        icon={<UserCog className="h-6 w-6" />}
        actions={
          isCurrentUserProtected && (
            <Button
              onClick={() => setAddingNew(!addingNew)}
              variant="adminPrimary"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Admin
            </Button>
          )
        }
      />

      {message && (
        <div className={`p-4 rounded ${
          message.includes('success') || message.includes('successfully') || message.includes('enabled') || message.includes('disabled')
            ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800'
            : 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800'
        }`}>
          {message}
        </div>
      )}

      {/* Add New User Form */}
      {addingNew && (
        <Card className="p-6 bg-gray-50 dark:bg-gray-800 border-2 border-red-200 dark:border-red-800">
          <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100 flex items-center">
            <Plus className="h-5 w-5 mr-2 text-red-600" />
            Add New Admin User
          </h3>
          <div className="space-y-4">
            <div>
              <Label htmlFor="new-email">Email Address</Label>
              <Input
                id="new-email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="user@example.com"
              />
            </div>
            <div>
              <Label htmlFor="new-name">Full Name</Label>
              <Input
                id="new-name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="John Doe"
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleAddUser} variant="adminPrimary">
                <Save className="h-4 w-4 mr-2" />
                Save User
              </Button>
              <Button
                onClick={() => {
                  setAddingNew(false);
                  setFormData({ email: '', name: '' });
                }}
                variant="outline"
              >
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Users List */}
      <div className="space-y-3">
        {users.map((user) => (
          <Card key={user.id} className={`p-4 ${user.is_protected ? 'border-2 border-yellow-400 dark:border-yellow-600 bg-yellow-50 dark:bg-yellow-900/30' : ''}`}>
            {/* Actions wrap below the identity on narrow screens instead of
                pushing the 44px touch targets off-screen (OWZ-72). */}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex min-w-0 items-center space-x-4 flex-1">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  user.is_protected ? 'bg-yellow-200 dark:bg-yellow-800' : 'bg-gray-200 dark:bg-gray-700'
                }`}>
                  {user.is_protected ? (
                    <Shield className="h-6 w-6 text-yellow-700" />
                  ) : (
                    <User className="h-6 w-6 text-gray-600 dark:text-gray-400" />
                  )}
                </div>

                <div className="flex-1">
                  {editingId === user.id ? (
                    <div className="space-y-2">
                      <Input
                        value={user.name}
                        onChange={(e) => handleEditName(user.id, e.target.value)}
                        className="max-w-xs"
                      />
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center space-x-2">
                        <h3 className="font-semibold text-lg">{user.name}</h3>
                        {user.is_protected && (
                          <span className="px-2 py-1 bg-yellow-200 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-200 text-xs font-semibold rounded">
                            PROTECTED
                          </span>
                        )}
                      </div>
                      <div className="flex items-center text-gray-600 dark:text-gray-400 text-sm">
                        <Mail className="h-4 w-4 mr-1" />
                        {user.email}
                      </div>
                      <div className="flex items-center text-gray-500 dark:text-gray-500 text-xs mt-1">
                        <Clock className="h-3 w-3 mr-1" />
                        Last login: {formatLastLogin(user.last_logged_in)}
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                {editingId === user.id ? (
                  <>
                    <Button
                      size="sm"
                      onClick={() => handleUpdateUser(user.id)}
                      variant="adminPrimary"
                    >
                      <Save className="h-4 w-4 mr-1" />
                      Save
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setEditingId(null)}
                    >
                      <X className="h-4 w-4 mr-1" />
                      Cancel
                    </Button>
                  </>
                ) : (
                  <>
                    {/* Edit button: protected admins can edit anyone, others can only edit themselves */}
                    {(isCurrentUserProtected || user.email === currentUser.email) && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setEditingId(user.id)}
                      >
                        <Edit2 className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                    )}
                    {/* Permissions and Delete: only for non-protected target users, and only by protected admins */}
                    {!user.is_protected && isCurrentUserProtected && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            if (editingPermissions === user.id) {
                              setEditingPermissions(null);
                            } else {
                              setEditingPermissions(user.id);
                              setTempPermissions(user.allowed_sections || []);
                            }
                          }}
                          className="border-amber-500 text-amber-700 hover:bg-amber-50"
                        >
                          <Shield className="h-4 w-4 mr-1" />
                          Permissions
                          {editingPermissions === user.id ? (
                            <ChevronUp className="h-3 w-3 ml-1" />
                          ) : (
                            <ChevronDown className="h-3 w-3 ml-1" />
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDeleteUser(user.id)}
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Delete
                        </Button>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>

            {user.is_protected && (
              <div className="mt-3 pt-3 border-t border-yellow-300 dark:border-yellow-700">
                <p className="text-xs text-yellow-800 dark:text-yellow-300">
                  <Shield className="h-3 w-3 inline mr-1" />
                  This is a protected admin account. Email cannot be changed and account cannot be deleted.
                  Only the name can be modified. Protected users have full access to all sections.
                </p>
              </div>
            )}

            {/* Release notes toggle - shown to protected admins for all users, or to self */}
            {(isCurrentUserProtected || user.email === currentUser.email) && (
              <div className={`mt-3 pt-3 border-t ${user.is_protected ? 'border-yellow-200 dark:border-yellow-800' : 'border-gray-200 dark:border-gray-700'}`}>
                <div className="flex items-center gap-3">
                  <Bell className={`h-4 w-4 ${user.is_protected ? 'text-yellow-700 dark:text-yellow-400' : 'text-gray-500 dark:text-gray-400'}`} />
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={user.receive_release_notes || false}
                      onChange={(e) => handleToggleReleaseNotes(user.id, user.email, e.target.checked)}
                      disabled={updatingReleaseNotesFor === user.id}
                      className="rounded border-gray-300 dark:border-gray-600 text-red-600 focus:ring-red-500"
                    />
                    <span className={`text-sm ${user.is_protected ? 'text-yellow-800 dark:text-yellow-300' : 'text-gray-700 dark:text-gray-300'}`}>
                      Receive weekly release notes email (Mondays)
                    </span>
                  </label>
                  {updatingReleaseNotesFor === user.id && (
                    <span className="text-xs text-gray-500 dark:text-gray-400 animate-pulse">
                      {user.receive_release_notes ? 'Disabling...' : 'Enabling & sending welcome email...'}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Permission summary for non-protected users */}
            {!user.is_protected && !editingPermissions && (
              <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  <Shield className="h-3 w-3 inline mr-1" />
                  {(user.allowed_sections || []).length > 0 ? (
                    <span>Access granted to <strong>{(user.allowed_sections || []).length}</strong> section(s)</span>
                  ) : (
                    <span className="text-orange-600 dark:text-orange-400">No section access granted</span>
                  )}
                </p>
              </div>
            )}

            {/* Permission editor panel */}
            {editingPermissions === user.id && (
              <div className="mt-4 pt-4 border-t border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/20 -mx-4 px-4 pb-4 -mb-4 rounded-b-lg">
                <h4 className="font-semibold mb-4 flex items-center text-gray-900 dark:text-gray-100">
                  <Shield className="h-4 w-4 mr-2 text-amber-600" />
                  Section Permissions
                </h4>

                <div className="space-y-4">
                  {Object.entries(adminRoutes).map(([catKey, category]) => {
                    const count = getCategoryPermissionCount(catKey);
                    const categorySections = category.sections;
                    const allSelected = count.selected === count.total;
                    const someSelected = count.selected > 0 && count.selected < count.total;
                    const CategoryIcon = category.icon;

                    return (
                      <div key={catKey} className="bg-background rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                        {/* Category header with checkbox */}
                        <div className="flex items-center mb-3">
                          <Checkbox
                            id={`cat-${user.id}-${catKey}`}
                            checked={allSelected}
                            ref={(el) => {
                              if (el) el.indeterminate = someSelected;
                            }}
                            onCheckedChange={() => handleToggleCategory(catKey)}
                            className="mr-2"
                          />
                          <CategoryIcon className="h-4 w-4 mr-2 text-gray-600 dark:text-gray-400" />
                          <label
                            htmlFor={`cat-${user.id}-${catKey}`}
                            className="font-medium text-gray-800 dark:text-gray-200 cursor-pointer flex-1"
                          >
                            {category.label}
                          </label>
                          <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                            {count.selected}/{count.total}
                          </span>
                        </div>

                        {/* Section checkboxes with icons */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 ml-6">
                          {categorySections.map(section => {
                            const Icon = section.icon;
                            const isChecked = tempPermissions.includes(section.id);
                            return (
                              <label
                                key={section.id}
                                className={`flex items-center p-2 rounded border cursor-pointer transition-colors ${
                                  isChecked
                                    ? 'border-amber-400 bg-amber-50 dark:bg-amber-900/30 dark:border-amber-600'
                                    : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
                                }`}
                              >
                                <Checkbox
                                  id={`section-${user.id}-${section.id}`}
                                  checked={isChecked}
                                  onCheckedChange={() => handleToggleSection(section.id)}
                                  className="mr-2"
                                />
                                <Icon className={`h-4 w-4 mr-2 ${isChecked ? 'text-amber-600' : 'text-gray-400'}`} />
                                <span className={`text-sm ${isChecked ? 'text-gray-900 dark:text-gray-100' : 'text-gray-600'}`}>
                                  {section.label}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Save/Cancel buttons */}
                <div className="flex gap-2 mt-4 pt-4 border-t border-amber-200">
                  <Button
                    onClick={() => handleSavePermissions(user.id)}
                    variant="adminPrimary"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Save Permissions
                  </Button>
                  <Button
                    onClick={() => setEditingPermissions(null)}
                    variant="outline"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </Card>
        ))}

        {users.length === 0 && (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            <User className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No admin users found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminUsersEditor;
